import React, { useState, useEffect } from 'react'
import { Button, Empty, Pagination, Modal } from 'antd'
import { Plus, CaretRight, BookmarkSimple, Trash } from '@phosphor-icons/react'
import { useGetAllActiveTemplates, useRemoveTemplate } from 'api/ScheduleTemplate/ScheduleTemplate'
import TemplateCreator from './TemplateCreator'
import usePagination from 'helper/usePagination'
import AppLayout from 'components/layout/layout'
import './Templates.scss'
import Loader from 'helper/Loader'

interface Template {
  _id: string
  templateName: string
  status: string
  organizationId: string
  tempActivities?: any[]
  activities?: any[]
  organization?: {
    _id: string
    name: string
    email: string
    status: string
  }
  createdAt: string
  updatedAt: string
  id?: string
}

const Templates: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([])
  const [loading, setLoading] = useState(false)
  const [showCreator, setShowCreator] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<Template | null>(null)
  const [totalCount, setTotalCount] = useState(0)
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
  })

  const { mutate: getTemplates } = useGetAllActiveTemplates()
  const { mutate: removeTemplate, isLoading: removingTemplate } = useRemoveTemplate()

  const [paginationInfo, , onPaginationChange] = usePagination({
    totalCount: totalCount,
    currentPage: params.page,
    setParams: setParams,
  })

  useEffect(() => {
    fetchTemplates()
  }, [params.page, params.limit])

  const fetchTemplates = () => {
    setLoading(true);

    getTemplates(params, {
      onSuccess: (response: any) => {
        const templatesData = response?.records || [];
        const total = response?.count || templatesData.length || 0;

        setTemplates(templatesData);
        setTotalCount(total);
        setLoading(false);
      },
      onError: () => {
        setTemplates([]);
        setTotalCount(0);
        setLoading(false);
      }
    });
  };

  const handleCreateTemplate = () => {
    setSelectedTemplate(null)
    setShowCreator(true)
  }

  const handleEditTemplate = (template: Template) => {
    setSelectedTemplate(template)
    setShowCreator(true)
  }

  const handleDeleteClick = (e: React.MouseEvent, template: Template) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      removeTemplate(templateToDelete._id, {
        onSuccess: () => {
          fetchTemplates();
          setDeleteModalOpen(false);
          setTemplateToDelete(null);
        }
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setTemplateToDelete(null);
  };

  const handleCreatorClose = () => {
    setShowCreator(false)
    setSelectedTemplate(null)
    fetchTemplates()
  }

  if (showCreator) {
    return (
      <TemplateCreator
        onClose={handleCreatorClose}
        template={selectedTemplate}
      />
    )
  }


  return (
    <AppLayout>
      <div className="templates-container">
        <div className="templates-header">
          <h2 className="templates-title"> Schedule Template</h2>
          <Button
            type="primary"
            icon={<Plus size={20} />}
            onClick={handleCreateTemplate}
            className="create-template-btn"
          >
            Create Template
          </Button>
        </div>

        <div className="templates-content">
          {loading ? (
            <Loader />
          ) : templates.length === 0 ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No templates found"
              className="empty-templates"
            >
              <Button
                type="primary"
                icon={<Plus size={20} />}
                onClick={handleCreateTemplate}
                size="large"
              >
                Create Your First Template
              </Button>
            </Empty>
          ) : (
            <>
              <div className="template-list">

                {templates.map((template, index) => {
                  const colors = ['#37A8C7', '#E85D8E', '#3B6EA5', '#7CB342'];

                  return (
                    <div
                      key={template._id}
                      className="template-card"
                      onClick={() => handleEditTemplate(template)}
                    >
                      <div className="template-left">
                        <div
                          className="template-icon"
                          style={{ backgroundColor: colors[index % colors.length] }}
                        >
                          <BookmarkSimple size={18} color="#fff" weight="fill" />
                        </div>

                        <div className="template-info">
                          <span className="template-name">{template.templateName}</span>

                          <span className="template-subtext">
                            {template.activities?.length || 0} Activities • {template.organization?.name || "Unknown"}
                          </span>
                        </div>
                      </div>

                      <div className="template-right" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          onClick={(e) => handleDeleteClick(e, template)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <Trash size={22} color="#ff4d4f" />
                        </button>
                        <CaretRight size={18} color="#999" />
                      </div>
                    </div>
                  );
                })}
              </div>
              {totalCount > (params.limit || 10) && (
                <div className="pagination-container" style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                  <Pagination
                    {...paginationInfo}
                    pageSize={params.limit || 10}
                    total={totalCount}
                    onChange={onPaginationChange}
                    showSizeChanger={false}
                    showQuickJumper
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} templates`}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Template"
        open={deleteModalOpen}
        onOk={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: false, loading: removingTemplate }}
        cancelButtonProps={{ className: 'btn-secondary' }}
        className="delete-confirmation-modal"
        centered
        width={480}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
          <div style={{
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            backgroundColor: '#ffebee',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '16px'
          }}>
            <Trash size={24} color="#ff6961" />
          </div>
          <div>
            <p style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#333' }}>
              Are you sure you want to delete <strong>({templateToDelete?.templateName})</strong> template?
            </p>
          </div>
        </div>
      </Modal>
    </AppLayout>
  )
}

export default Templates
